import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../store/appStore';
import { Button } from '../components/ui/button';
import { ServiceCard, CourseCard } from '../components/Cards';
import { MapView } from '../components/MapView';
import { Users, Heart, BookOpen, GraduationCap, ChevronRight, Award, Sparkles } from 'lucide-react';

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const { homeStats, fetchHomeStats } = useAppStore();
  const language = i18n.language;

  useEffect(() => {
    fetchHomeStats();
  }, [fetchHomeStats]);

  const stats = [
    { icon: Users, label: t('home.teachers'), count: homeStats?.counts?.teachers || 0 },
    { icon: BookOpen, label: t('home.schools'), count: homeStats?.counts?.schools || 0 },
    { icon: Heart, label: t('home.services'), count: homeStats?.counts?.services || 0 },
    { icon: GraduationCap, label: t('home.courses'), count: homeStats?.counts?.courses || 0 },
  ];

  return (
    <div className="animate-fade-in" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative py-16 px-4 text-center bg-gradient-to-b from-cream-dark to-cream" style={{ background: 'linear-gradient(to bottom, #F5EFE6, #FDFBF7)' }}>
        <div className="max-w-4xl mx-auto">
          <h1 className="font-serif text-5xl md:text-6xl font-bold text-primary mb-6" data-testid="hero-title">{t('home.welcome')}</h1>
          <p className="text-xl text-secondary mb-8" data-testid="hero-subtitle">{t('home.subtitle')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/services">
              <Button className="bg-gold hover:bg-gold/90 text-white px-8 py-3 text-lg" style={{ backgroundColor: '#D4A574' }} data-testid="explore-services-btn">
                {t('home.exploreServices')}
              </Button>
            </Link>
            <Link to="/courses">
              <Button variant="outline" className="border-gold text-gold hover:bg-gold hover:text-white px-8 py-3 text-lg" style={{ borderColor: '#D4A574', color: '#D4A574' }} data-testid="explore-courses-btn">
                {t('home.exploreCourses')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Interactive Map - Full Width */}
      <section className="py-8 px-4" data-testid="map-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-2xl font-bold text-primary mb-4 text-center">{t('home.findNearYou') || 'Encuentra profesionales cerca de ti'}</h2>
          <div className="h-[400px] rounded-2xl overflow-hidden shadow-lg">
            <MapView compact={false} />
          </div>
        </div>
      </section>

      {/* Featured Services */}
      {homeStats?.featured?.services?.length > 0 && (
        <section className="py-12 px-4" data-testid="featured-services-section">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-8">
              <h2 className="font-serif text-3xl font-bold text-primary">{t('home.featuredServices')}</h2>
              <Link to="/services" className="text-gold flex items-center hover:underline" style={{ color: '#D4A574' }}>
                {t('common.viewAll')} <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {homeStats.featured.services.map((service) => (
                <ServiceCard key={service.id} service={service} language={language} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Courses */}
      {homeStats?.featured?.courses?.length > 0 && (
        <section className="py-12 px-4 bg-cream-dark" style={{ backgroundColor: '#F5EFE6' }} data-testid="featured-courses-section">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-8">
              <h2 className="font-serif text-3xl font-bold text-primary">{t('home.featuredCourses')}</h2>
              <Link to="/courses" className="text-gold flex items-center hover:underline" style={{ color: '#D4A574' }}>
                {t('common.viewAll')} <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {homeStats.featured.courses.map((course) => (
                <CourseCard key={course.id} course={course} language={language} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Call to Action - Join Platform */}
      <section className="py-16 px-4" data-testid="cta-section">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-primary to-primary/90 rounded-3xl p-8 md:p-12 text-center text-white shadow-xl" style={{ background: 'linear-gradient(135deg, #2C3E50 0%, #34495E 100%)' }}>
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center">
                <Sparkles className="h-8 w-8 text-gold" style={{ color: '#D4A574' }} />
              </div>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">
              ¿Eres profesional del tantra o tienes una escuela?
            </h2>
            <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
              Únete a nuestra comunidad y conecta con personas que buscan tus servicios. 
              Publica tus sesiones, cursos y retiros para llegar a más clientes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button className="bg-gold hover:bg-gold/90 text-white px-8 py-3 text-lg" style={{ backgroundColor: '#D4A574' }}>
                  <Award className="h-5 w-5 mr-2" />
                  Registrarme como Profesional
                </Button>
              </Link>
              <Link to="/register">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-primary px-8 py-3 text-lg">
                  <BookOpen className="h-5 w-5 mr-2" />
                  Registrar mi Escuela
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats - Compact Footer Style */}
      <section className="py-8 px-4 bg-cream-dark border-t" style={{ backgroundColor: '#F5EFE6' }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center items-center gap-8 md:gap-16 flex-wrap">
            {stats.map((stat, idx) => (
              <div key={idx} className="flex items-center gap-3" data-testid={`stat-${idx}`}>
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
                  <stat.icon className="h-5 w-5 text-gold" style={{ color: '#D4A574' }} />
                </div>
                <div>
                  <p className="text-xl font-bold text-primary">{stat.count}</p>
                  <p className="text-xs text-secondary">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
